// @flow
import type { Theme } from '@mui/material/styles';

// Base constants that can be overridden
const TABLE_CONSTANTS = {
  MIN_WIDTH: 800,
  ROW_SPACING: 0.5,
  ROW_SHADOW_REST: '0px 2px 8px -1px rgba(0,0,0,0.2)',
  ROW_SHADOW_HOVER: '0px 2px 8px -1px rgba(0,0,0,0.8)',
  MOBILE_ITEM_HEIGHT: 'auto',
  ACTION_BTN_SIZE: 28
};

/**
 * Base table styles that provide the foundation for all data tables
 * Includes hover mechanics, row layout, and action button behavior
 */
const baseTableStyles = {
  // Container styles
  tableContainer: (theme) => ({
    flexGrow: 1,
    width: '100%',
    [theme.breakpoints.down('sm')]: {
      minWidth: 0,
      padding: theme.spacing(2),
      paddingTop: theme.spacing(1)
    }
  }),

  tableContent: (theme) => ({
    flexGrow: 1,
    padding: theme.spacing(3),
    width: '100%',
    minWidth: TABLE_CONSTANTS.MIN_WIDTH,
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(1),
      minWidth: 'auto'
    }
  }),

  // Core table structure
  table: (theme) => ({
    tableLayout: 'fixed',
    borderCollapse: 'separate',
    borderSpacing: 0,
    padding: 0,
    width: '100%',
    display: 'block',
    whiteSpace: 'nowrap',
    '& thead, & tbody': {
      display: 'block'
    },
    '& tr:first-of-type': {
      borderTop: 'none'
    }
  }),

  tableBody: (theme) => ({
    display: 'flex',
    flexDirection: 'column',
    width: `calc(100vw - ${theme.layout.customProps.drawer.width}px - ${theme.spacing(6)}px)`,
    [theme.breakpoints.down('sm')]: {
      width: `calc(100vw - ${theme.layout.customProps.drawer.width}px - ${theme.spacing(2)}px)`,
      minWidth: '100%'
    }
  }),

  // Header row
  tableHeadRow: (theme) => ({
    display: 'flex',
    position: 'relative',
    height: 30,
    marginBottom: theme.spacing(1),
    '& th': {
      fontSize: '0.9rem',
      fontWeight: 600,
      borderBottom: 0,
      backgroundColor: 'transparent',
      cursor: 'pointer'
    }
  }),

  // Base row with hover mechanics
  tableRow: (theme) => ({
    display: 'flex',
    borderRadius: theme.shape.borderRadius,
    overflow: 'hidden',
    flexGrow: 1,
    zIndex: 2,
    margin: `${theme.spacing(TABLE_CONSTANTS.ROW_SPACING)} 0`,
    marginBottom: theme.spacing(2),
    position: 'relative',
    boxShadow: TABLE_CONSTANTS.ROW_SHADOW_REST,
    background: theme.palette.table.cellBackground,
    cursor: 'pointer',
    
    // Hover state for entire row
    '&.hover': {
      boxShadow: TABLE_CONSTANTS.ROW_SHADOW_HOVER
    },
    
    // Action cell behavior - hidden by default, shown on hover
    '& td.actionCell': {
      display: 'none'
    },
    
    '&.hover td.actionCell': {
      display: 'flex',
      padding: '0 !important',
      margin: '0 !important',
      border: 'none !important',
      backgroundColor: theme.palette.table.cellBackground, // Match row background exactly
      justifyContent: 'flex-end', // Right align buttons (normal order)
      alignItems: 'stretch', // Stretch buttons to full height
      flexDirection: 'row', // Reverse button order to fix the sequence
      minWidth: `${theme.layout.listTable.rowHeight * 4}px`, // 4 buttons max width
      maxWidth: `${theme.layout.listTable.rowHeight * 4}px`,
      '& button': {
        backgroundColor: `${theme.palette.primary.lighter}`, // Apply light blue to buttons
        color: 'white',
        borderRadius: '0', // No border radius to fill height completely
        height: '100%', // Full row height
        minHeight: `${theme.layout.listTable.rowHeight}px`,
        width: `${theme.layout.listTable.rowHeight}px`,
        minWidth: `${theme.layout.listTable.rowHeight}px`,
        margin: '0',
        border: 'none',
        '&:hover': {
          backgroundColor: `${theme.palette.primary.main}`
        },
        '&:last-child': {
          borderTopRightRadius: `${theme.shape.borderRadius}px`,
          borderBottomRightRadius: `${theme.shape.borderRadius}px`
        }
      }
    },
    
    // Opacity effects for hover states
    '&.hover .hoverOpacity': {
      opacity: 1
    },
    
    // Border radius management
    '& .lastChild': {
      borderBottomRightRadius: theme.shape.borderRadius,
      borderTopRightRadius: theme.shape.borderRadius
    }
  }),

  // Base cell styling
  cell: (theme) => ({
    display: 'flex',
    padding: `${theme.spacing(2)}px ${theme.spacing(1)}px`,
    flex: '0 0 auto',
    lineHeight: theme.typography.body2.lineHeight,
    alignItems: 'center',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    border: 'none',
    borderTop: 0,
    borderBottom: 0,
    background: 'white',
    zIndex: 0,
    '&:first-of-type': {
      borderLeft: 0,
      borderTopLeftRadius: theme.shape.borderRadius,
      borderBottomLeftRadius: theme.shape.borderRadius
    },
    '&:last-of-type': {
      borderRight: 0,
      borderBottomRightRadius: theme.shape.borderRadius,
      borderTopRightRadius: theme.shape.borderRadius
    }
  }),

  // Common cell modifiers
  clickableCell: (theme) => ({
    cursor: 'default'
  }),

  textOverflow: (theme) => ({
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    [theme.breakpoints.down('xs')]: {
      overflow: 'scroll'
    }
  }),

  // Action button styling - Desktop/Table version
  actionButton: (theme) => ({
    borderRadius: 0,
    height: theme.layout.listTable.rowHeight,
    width: theme.layout.listTable.rowHeight,
    minWidth: theme.layout.listTable.rowHeight,
    backgroundColor: theme.palette.primary.lighter,
    color: 'white',
    margin: 0,
    border: 'none',
    '&:hover': {
      backgroundColor: theme.palette.primary.main
    },
    '&:last-child': {
      borderTopRightRadius: `${theme.shape.borderRadius}px`,
      borderBottomRightRadius: `${theme.shape.borderRadius}px`
    }
  }),

  // Mobile styles
  mobileContainer: (theme) => ({
    borderRadius: theme.shape.borderRadius / 2,
    boxShadow: TABLE_CONSTANTS.ROW_SHADOW_REST,
    height: TABLE_CONSTANTS.MOBILE_ITEM_HEIGHT,
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    padding: `${theme.spacing(2)}px ${theme.spacing(2)}px ${theme.spacing(1.5)}px ${theme.spacing(2)}px`,
    '&.hover': {
      boxShadow: TABLE_CONSTANTS.ROW_SHADOW_HOVER
    }
  }),

  mobileHeader: (theme) => ({
    padding: `${theme.spacing(1)}px ${theme.spacing(2)}px ${theme.spacing(0.5)}px ${theme.spacing(2)}px`,
    fontSize: '0.8rem'
  }),

  mobileRowContainer: (theme) => ({
    flexWrap: 'nowrap',
    minHeight: 45,
    marginBottom: 0,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    '& p': {
      fontSize: '0.9rem'
    }
  }),

  mobileActionButton: (theme) => ({
    margin: theme.spacing(0.25),
    borderRadius: TABLE_CONSTANTS.ACTION_BTN_SIZE,
    height: TABLE_CONSTANTS.ACTION_BTN_SIZE,
    width: TABLE_CONSTANTS.ACTION_BTN_SIZE,
    minWidth: TABLE_CONSTANTS.ACTION_BTN_SIZE,
    color: 'white',
    backgroundColor: theme.palette.primary.lighter,
    '& i, & *': {
      fontSize: '1rem'
    },
    '&:hover': {
      backgroundColor: theme.palette.primary.main
    }
  }),

  // Configurable action button - can be used for both desktop and mobile
  configurableActionButton: (theme, options = {}) => ({
    backgroundColor: theme.palette.primary.lighter,
    color: 'white',
    border: 'none',
    '&:hover': {
      backgroundColor: theme.palette.primary.main
    },
    // Desktop/table defaults
    borderRadius: options.mobile ? TABLE_CONSTANTS.ACTION_BTN_SIZE : 0,
    height: options.mobile ? TABLE_CONSTANTS.ACTION_BTN_SIZE : theme.layout.listTable.rowHeight,
    width: options.mobile ? TABLE_CONSTANTS.ACTION_BTN_SIZE : theme.layout.listTable.rowHeight,
    minWidth: options.mobile ? TABLE_CONSTANTS.ACTION_BTN_SIZE : theme.layout.listTable.rowHeight,
    margin: options.mobile ? theme.spacing(0.25) : 0,
    // Mobile icon sizing
    ...(options.mobile && {
      '& i, & *': {
        fontSize: '1rem'
      }
    }),
    // Desktop last-child border radius
    ...(!options.mobile && {
      '&:last-child': {
        borderTopRightRadius: `${theme.shape.borderRadius}px`,
        borderBottomRightRadius: `${theme.shape.borderRadius}px`
      }
    })
  }),

  // Results and pagination
  resultsCount: (theme) => ({
    color: '#5b5b5b',
    fontSize: '0.8rem',
    marginBottom: '1rem',
    paddingLeft: theme.spacing(1)
  }),

  // Sort arrows
  sortArrow: (theme) => ({
    fontSize: '1.2rem',
    position: 'relative',
    top: 1,
    marginLeft: theme.spacing(0.375),
    color: '#66666a',
    verticalAlign: 'text-bottom'
  }),

  // Enhanced table container with auto-sizing
  configurableTableContainer: (theme) => ({
    flexGrow: 1,
    width: '100%',
    height: 'auto', // Ensure table can expand to content
    minHeight: 'auto', // Remove any minimum height constraints
    [theme.breakpoints.down('sm')]: {
      minWidth: 0,
      padding: 0
    }
  })
};

export default baseTableStyles;
export { TABLE_CONSTANTS }; 